import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { ArrowLeft, X, Plus, FileText, Mail, MapPin, AlertCircle, CheckCircle, Loader2, Edit3, Download } from 'lucide-react';
import { cn, downloadFile } from '../../../../../lib/utils';
import { useReportGeneration } from '../../../../../redux/WeldData/hooks/useWeldData';
import { Notification } from '../../../../../components/ui/custom-notification';
import { ReportColumnConfigModal } from './ReportColumnConfigModal';
import { getReportColumns } from '../../../../../services/reportColumnsApi';
import { logProductionReportGenerated } from '../../../utils/productionAudit';
import { useProductionModalAudit } from '../../../../audit/useProductionModalAudit';

interface ReportGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerateReport: (reportType: string, emails: string[]) => void;
  projectName?: string;
  jobNumber?: string;
  weldNumbers?: string[]; // Add weld IDs from filtered data (keeping parameter name as weldNumbers)
  projectId?: number; // Add project ID for API calls
  selectedWeldNumbersForReport?: string[];
}

export const ReportGenerationModal: React.FC<ReportGenerationModalProps> = ({
  isOpen,
  onClose,
  onGenerateReport,
  projectName,
  jobNumber,
  weldNumbers = [],
  selectedWeldNumbersForReport = [],
  projectId
}) => {
  useProductionModalAudit(isOpen, 'Generate Report', projectId, projectName);

  const [reportType, setReportType] = useState<string>('t-logs');
  const [reportVersion, setReportVersion] = useState<string>('v1');
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [showEmailRequired, setShowEmailRequired] = useState<boolean>(false);
  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const [storedDownloadData, setStoredDownloadData] = useState<{ url: string, reportType: string } | null>(null);

  // Helper function to extract download URL from various response structures
  const getDownloadUrl = (response: any) => {
    if (!response) return null;

    // Try direct property first
    if (response.downloadUrl) {
      return response.downloadUrl;
    }

    // Try nested data
    if (response.nestedData?.data?.downloadUrl) {
      return response.nestedData.data.downloadUrl;
    }
    if (response.nestedData?.downloadUrl) {
      return response.nestedData.downloadUrl;
    }

    // Try data.data.data.data structure (the actual structure from the API)
    if (response.data?.data?.data?.data?.downloadUrl) {
      return response.data.data.data.data.downloadUrl;
    }

    // Try data.data.data structure
    if (response.data?.data?.data?.downloadUrl) {
      return response.data.data.data.downloadUrl;
    }

    // Try data.data structure
    if (response.data?.data?.downloadUrl) {
      return response.data.data.downloadUrl;
    }

    return null;
  };

  const handleDownload = () => {
    if (downloadUrl) {
      // Create a hidden link and click it to download without focus switch
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  const [isColumnConfigModalOpen, setIsColumnConfigModalOpen] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(['current', 'volts', 'travel_speed', 'wire_speed', 'heat']);
  const [projectColumns, setProjectColumns] = useState<string[]>([]);
  const [isLoadingColumns, setIsLoadingColumns] = useState<boolean>(false);
  const auditLoggedForSuccessRef = useRef(false);

  const {
    reportGenerationLoading,
    reportGenerationSuccess,
    reportGenerationError,
    reportGenerationData,
    generateReport,
    clearReportStatus
  } = useReportGeneration();

  // Function to get consistent description for all report types
  const getReportDescription = (reportType: string) => {
    const baseDescription = reportType === 't-logs' ? 'Generate T-Logs report' :
      reportType === 'pass-view' ? 'Generate Pass View report' :
        'Generate Zone View report';

    if (weldNumbers.length > 0) {
      return `${baseDescription} for selected welds`;
    }

    return `${baseDescription} for entire project`;
  };

  const reportTypes = useMemo(() => [
    { value: 't-logs', label: 'T-Logs Report', icon: FileText, description: getReportDescription('t-logs') },
    { value: 'pass-view', label: 'Pass View Report', icon: Mail, description: getReportDescription('pass-view') },
    { value: 'zone-view', label: 'Zone View Report', icon: MapPin, description: getReportDescription('zone-view') }
  ], [weldNumbers]);

  // Clear report status when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      clearReportStatus();
      setShowEmailRequired(false);
      setDownloadUrl('');
      setStoredDownloadData(null);
      if (projectId) {
        loadProjectColumns();
      }
    }
  }, [isOpen, clearReportStatus, projectId]);

  // Show/hide download button based on report type match
  useEffect(() => {
    if (storedDownloadData) {
      if (storedDownloadData.reportType === reportType) {
        setDownloadUrl(storedDownloadData.url);
      } else {
        setDownloadUrl('');
      }
    }
  }, [reportType, storedDownloadData]);

  // Function to load project's column configuration
  const loadProjectColumns = async () => {
    if (!projectId) return;

    setIsLoadingColumns(true);
    try {
      const response = await getReportColumns(projectId);
      const reportColumns = response.data.project.report_columns;

      // Get enabled columns from the API response
      const enabledColumns = Object.entries(reportColumns)
        .filter(([key, value]) => key !== 'is_high_low_avg_required' && value === true)
        .map(([key]) => key);

      setProjectColumns(enabledColumns);
    } catch (error) {
      // Fallback to default columns on error
      setProjectColumns(['current', 'volts', 'travel_speed', 'wire_speed', 'heat']);
    } finally {
      setIsLoadingColumns(false);
    }
  };

  // Watch for successful report generation
  useEffect(() => {
    if (!reportGenerationSuccess) {
      auditLoggedForSuccessRef.current = false;
      return;
    }

    if (reportGenerationSuccess && reportGenerationData && isOpen && !auditLoggedForSuccessRef.current) {
      auditLoggedForSuccessRef.current = true;
      const isDownloadReport = reportVersion === 'v2' && (reportType === 'pass-view' || reportType === 'zone-view');
      const generatedDownloadUrl = getDownloadUrl(reportGenerationData);

      logProductionReportGenerated({
        projectId: projectId ?? null,
        projectName: projectName ?? null,
        jobNumber: jobNumber ?? null,
        reportType,
        reportVersion,
        weldNumbers,
        emails,
        downloadUrl: generatedDownloadUrl,
        deliveryMethod: isDownloadReport && generatedDownloadUrl ? 'download' : 'email',
      });

      // Check if this is a v2 response with download URL
      if (isDownloadReport && reportGenerationData.isV2 && generatedDownloadUrl) {
        // For v2, automatically open PDF in new tab
        const downloadUrl = generatedDownloadUrl;
        const fileName = downloadUrl.split('/').pop() || 'report.pdf';


        // Store download URL and report type for download button
        setDownloadUrl(downloadUrl);
        setStoredDownloadData({
          url: downloadUrl,
          reportType: reportGenerationData.reportType
        });

        // Show success notification
        Notification.success('Success', `Report generated successfully! Click the download button to get your report.`);

        // Don't clear success state automatically - let user control when to generate new report
      } else {
        // For v1, show email notification
        Notification.success('Success', 'Report generation initiated successfully! Check your email for the report.');
        clearReportStatus();
        onClose();
      }
    }
  }, [reportGenerationSuccess, reportGenerationData, isOpen, reportVersion, reportType, clearReportStatus, onClose]);

  // Watch for report generation errors
  useEffect(() => {
    if (reportGenerationError && isOpen) {
      // Check if it's the email requirement error
      if (reportGenerationError.includes('email address') || reportGenerationError.includes('Report generation will take approximately')) {
        setShowEmailRequired(true);
        // Extract the time from the error message (e.g., "3 minutes")
        const timeMatch = reportGenerationError.match(/(\d+)\s+minutes?/);
        const timeText = timeMatch ? `${timeMatch[1]} minutes` : 'a few minutes';

        Notification.error('Email Required', `This report will take approximately ${timeText} to generate. Please add an email address to receive the report when ready.`);
      } else {
        Notification.error('Report Generation Failed', reportGenerationError);
      }
    }
  }, [reportGenerationError, isOpen]);

  const handleAddEmail = () => {
    if (!newEmail.trim()) {
      setEmailError('Please enter an email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (emails.includes(newEmail.trim())) {
      setEmailError('Email address already exists');
      return;
    }

    const trimmedEmail = newEmail.trim();
    setEmails([...emails, trimmedEmail]);
    setNewEmail('');
    setEmailError('');
    setShowEmailRequired(false); // Clear the email required indicator when email is added

    // Debug: Log email addition

  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter(email => email !== emailToRemove));


  };

  const handleGenerateReport = () => {
    // For v2 (Download) reports, email is not required
    const isV2Report = reportVersion === 'v2' && (reportType === 'pass-view' || reportType === 'zone-view');

    if (!isV2Report && emails.length === 0) {
      setEmailError('Please add at least one email address');
      return;
    }

    // Validate all emails are still valid (only for v1 or when emails are provided)
    if (emails.length > 0) {
      const invalidEmails = emails.filter(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
      if (invalidEmails.length > 0) {
        setEmailError(`Invalid email addresses: ${invalidEmails.join(', ')}`);
        return;
      }
    }

    // Clear any previous errors and download URL
    setEmailError('');
    setDownloadUrl('');
    setStoredDownloadData(null);

    // For v2 reports, don't use any email if none provided
    const emailsToUse = emails;
    console.log('🔄 ReportGenerationModal - handleGenerateReport', weldNumbers);
    // Use the Redux action instead of the prop callback
    // TypeScript workaround: hook accepts any value but types are inferred strictly
    (generateReport as (reportType: string, emailAddress: string | string[], weldNumbers: string[], reportVersion: string, projectId: number | null | undefined) => void)(
      reportType, 
      emailsToUse, 
      weldNumbers, 
      reportVersion, 
      projectId
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddEmail();
    }
  };

  const handleColumnConfigSave = (columns: string[]) => {
    setSelectedColumns(columns);
    setProjectColumns(columns); // Update project columns to reflect the changes
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          "w-[95vw] max-w-[1400px] min-w-[320px] max-h-[85vh] p-0 gap-0 border bg-white shadow-2xl overflow-hidden flex flex-col",
          "sm:w-[90vw] sm:max-w-[800px]",
          "md:w-[85vw] md:max-w-[1000px]",
          "lg:w-[80vw] lg:max-w-[1200px]",
          "xl:w-[75vw] xl:max-w-[1400px]"
        )}
      >
        <div className="flex flex-col min-h-0 flex-1 relative z-20">
          <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white relative z-10 flex-shrink-0">
            <div className="flex items-center gap-4">
              <Button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                variant="outline"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div>
                <DialogTitle className="text-lg font-bold text-gray-900">
                  Report Generation
                  {reportGenerationLoading && (
                    <span className="ml-2 text-sm font-normal text-red-600">
                      (Generating...)
                    </span>
                  )}
                </DialogTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {projectName && jobNumber ? `Project: ${projectName} | Project Number: ${jobNumber}` :
                    projectName ? `Project: ${projectName}` :
                      jobNumber ? `Project Number: ${jobNumber}` : ''}
                  {selectedWeldNumbersForReport && selectedWeldNumbersForReport.length > 0 && (
                    <span className="ml-2">
                      | {selectedWeldNumbersForReport.length} Welds Selected
                    </span>
                  )}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0">
            <div className="space-y-8">

              {/* Filter Status */}




              {/* Report Type Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-gray-700">Report Type</Label>
                  <Button
                    onClick={() => setIsColumnConfigModalOpen(true)}
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 text-xs"
                    disabled={reportGenerationLoading}
                  >
                    <Edit3 className="h-3 w-3 mr-1" />
                    Configure Columns
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {reportTypes.map((type) => {
                    const IconComponent = type.icon;

                    return (
                      <label
                        key={type.value}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${reportType === type.value
                            ? ' bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <input
                          type="radio"
                          name="reportType"
                          value={type.value}
                          checked={reportType === type.value}
                          onChange={(e) => setReportType(e.target.value)}
                          disabled={reportGenerationLoading}
                          className="h-4 w-4 text-red-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-red-600"
                        />
                        <IconComponent className="h-5 w-5 text-gray-600" />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">{type.label}</span>
                          <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Version Selection - Only show for Pass View and Zone View */}
                {(reportType === 'pass-view' || reportType === 'zone-view') && (
                  <div className="space-y-3 mt-4">
                    <Label className="text-sm font-medium text-gray-700">Report Version</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${reportVersion === 'v1'
                            ? ' bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <input
                          type="radio"
                          name="reportVersion"
                          value="v1"
                          checked={reportVersion === 'v1'}
                          onChange={(e) => setReportVersion(e.target.value)}
                          disabled={reportGenerationLoading}
                          className="h-4 w-4 text-red-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-red-600"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Version 1 (Email)</span>
                          <p className="text-xs text-gray-500 mt-1">Report sent directly to email after generation</p>
                        </div>
                      </label>
                      <label
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${reportVersion === 'v2'
                            ? ' bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <input
                          type="radio"
                          name="reportVersion"
                          value="v2"
                          checked={reportVersion === 'v2'}
                          onChange={(e) => setReportVersion(e.target.value)}
                          disabled={reportGenerationLoading}
                          className="h-4 w-4 text-red-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-red-600"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-900">Version 2 (Download)</span>
                          <p className="text-xs text-gray-500 mt-1">Get download link to access report immediately</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

              </div>

              {/* Email Input */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className={`text-sm font-medium ${showEmailRequired ? 'text-red-700' : 'text-gray-700'}`}>
                    Email Addresses
                    {showEmailRequired && (
                      <span className="ml-2 text-xs text-red-600 font-medium">(Required for this report)</span>
                    )}
                    {!showEmailRequired && emails.length > 0 && (
                      <span className="ml-2 text-xs text-gray-500">({emails.length} added)</span>
                    )}
                  </Label>

                  {/* Existing Emails */}
                  {emails.length > 0 && (
                    <div className="space-y-2">
                      {emails.map((email, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-600">{index + 1}.</span>
                          <span className="text-sm text-gray-900 flex-1">{email}</span>
                          <Button
                            onClick={() => handleRemoveEmail(email)}
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 hover:bg-gray-200"
                            disabled={reportGenerationLoading}
                          >
                            <X className="h-4 w-4 text-gray-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Email */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="flex-1">
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className={cn(
                          showEmailRequired ? ' focus:ring-red-500' : '',
                          emailError ? '' : ''
                        )}
                        disabled={reportGenerationLoading}
                      />
                      {emailError && (
                        <p className="text-xs text-red-600 mt-1">{emailError}</p>
                      )}
                    </div>
                    <Button
                      onClick={handleAddEmail}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3"
                      disabled={reportGenerationLoading}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Additional Info Section */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700">Report Information</Label>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="space-y-3 text-sm text-blue-700">
                      {reportVersion === 'v2' && (reportType === 'pass-view' || reportType === 'zone-view') ? (
                        <>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            <span>Download link opens in new browser tab</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            <span>Report available immediately after generation</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            <span>Maximum 20 welds per report</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            <span>No email required for download version</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            <span>Email delivery to specified recipients</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            <span>Processing time: 2-5 minutes</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            <span>Maximum 20 welds per report</span>
                          </div>
                        </>
                      )}

                      {/* Selected Columns Information */}
                      {(projectColumns.length > 0 || isLoadingColumns) && (
                        <div className="pt-2 border-t">
                          {isLoadingColumns ? (
                            <div className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              <span>Loading column configuration...</span>
                            </div>
                          ) : (
                            <div>
                              {reportType === 't-logs' ? (
                                <div className="flex items-center gap-2">
                                  <AlertCircle className="h-4 w-4 text-orange-500" />
                                  <span className="text-orange-600 font-medium">
                                    Note: Configured columns do not apply to T-Logs reports
                                  </span>
                                </div>
                              ) : (
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <FileText className="h-4 w-4" />
                                    <span className="font-medium">Selected columns:</span>
                                  </div>
                                  <p className="text-blue-600 ml-6">
                                    {projectColumns.map((columnId) => {
                                      const columnLabels: { [key: string]: string } = {
                                        'summary_page': 'Summary Page',
                                        'current': 'Current',
                                        'volts': 'Volts',
                                        'frequency': 'Frequency',
                                        'target': 'Target',
                                        'horizontal_bias': 'Horizontal Bias',
                                        'arc_impedance': 'Arc Impedance',
                                        'travel_speed': 'Travel Speed',
                                        'wire_speed': 'Wire Speed',
                                        'heat': 'Heat Input',
                                        'true_energy': 'True Energy',
                                        'oscillation_width': 'Oscillation Width',
                                        'total_wire_consumed': 'Total Wire Consumed'
                                      };
                                      return columnLabels[columnId] || columnId;
                                    }).join(', ')}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Messages */}
              {reportGenerationSuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-green-700">Report generation initiated successfully!</span>
                </div>
              )}

              {/* Download button for successful report generation */}
              {downloadUrl && storedDownloadData && storedDownloadData.reportType === reportType && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div className="flex-1">
                    <div className="text-sm text-green-700 font-medium">Report generated successfully!</div>
                    <div className="text-xs text-green-600 mt-1">Click download to get your report</div>
                  </div>
                  <Button
                    onClick={handleDownload}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              )}


              {/* No success message display - only notification popup */}

              {reportGenerationError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <div className="text-sm text-red-700">
                    <div className="font-medium">Error: {reportGenerationError}</div>
                    {reportGenerationError.includes('email address') && (
                      <div className="mt-1 text-xs text-red-600">
                        Please add an email address above to receive the report when it's ready.
                      </div>
                    )}
                    {reportGenerationError.includes('Report generation will take approximately') && (
                      <div className="mt-1 text-xs text-red-600">
                        This is a long-running report. The system will email you when it's ready.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-6 border-t">
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="px-6"
                >
                  Cancel
                </Button>

                {/* Show retry button when there's an error */}
                {reportGenerationError && (
                  <Button
                    onClick={() => {
                      clearReportStatus();
                      setShowEmailRequired(false);
                    }}
                    variant="outline"
                    className="px-6"
                  >
                    Try Again
                  </Button>
                )}

                {/* Always show generate button - modal stays open */}
                <Button
                  onClick={handleGenerateReport}
                  className="bg-red-600 hover:bg-red-700 text-white px-6"
                  disabled={
                    reportGenerationLoading ||
                    (!(reportVersion === 'v2' && (reportType === 'pass-view' || reportType === 'zone-view')) && emails.length === 0)
                  }
                >
                  {reportGenerationLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : reportVersion === 'v2' && (reportType === 'pass-view' || reportType === 'zone-view') ? (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Generate Report URL
                    </>
                  ) : (
                    'Generate Report'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Column Configuration Modal */}
      <ReportColumnConfigModal
        isOpen={isColumnConfigModalOpen}
        onClose={() => setIsColumnConfigModalOpen(false)}
        onSave={handleColumnConfigSave}
        initialSelectedColumns={selectedColumns}
        projectId={projectId}
        projectName={projectName}
      />
    </Dialog>
  );
};

